import { useEffect, useState } from 'react';
import { Badge } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';
import { Alert } from '@/components/UI/Alert';
import { Modal } from '@/components/UI/Modal';
import { fieldClass } from '@/components/UI/fieldStyles';
import { cn } from '@/utils/cn';
import { useListActivityEventsQuery, type ActivityEvent } from '@/redux/internal-services/platformLogsApi';
import {
  DetailRow,
  JsonBlock,
  PAGE_SIZE,
  PagedFooter,
  TraceLink,
  fmtTime,
  toIso,
  type SharedLogFilters,
} from './shared';

const humanize = (s: string) => s.replace(/[_.]/g, ' ');

export default function ActivityLogTab({ tenantId, fromDate, toDate, onTrace }: SharedLogFilters) {
  const [resourceType, setResourceType] = useState('');
  const [resourceId, setResourceId] = useState('');
  const [actor, setActor] = useState('');
  const [skip, setSkip] = useState(0);
  const [selected, setSelected] = useState<ActivityEvent | null>(null);

  useEffect(() => setSkip(0), [tenantId, fromDate, toDate, resourceType, resourceId, actor]);

  const { data, isFetching, isError } = useListActivityEventsQuery({
    tenant_id: tenantId || undefined,
    from_date: toIso(fromDate),
    to_date: toIso(toDate),
    resource_type: resourceType || undefined,
    resource_id: resourceId || undefined,
    actor_user_id: actor || undefined,
    limit: PAGE_SIZE,
    skip,
  });
  const rows = data?.data ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <input aria-label="Filter by record type" placeholder="Record type (e.g. task)" value={resourceType}
          onChange={(e) => setResourceType(e.target.value)} className={cn(fieldClass({ size: 'sm' }), 'w-44')} />
        <input aria-label="Filter by record ID" placeholder="Record ID" value={resourceId}
          onChange={(e) => setResourceId(e.target.value)} className={cn(fieldClass({ size: 'sm' }), 'w-48')} />
        <input aria-label="Filter by user ID" placeholder="User ID" value={actor}
          onChange={(e) => setActor(e.target.value)} className={cn(fieldClass({ size: 'sm' }), 'w-48')} />
        <span className="text-xs text-ui-text3">{total} results</span>
      </div>

      {isError && <Alert tone="danger">Could not load activity.</Alert>}

      <div className="table-card">
        {isFetching ? (
          <LoadingState label="Loading..." />
        ) : rows.length === 0 ? (
          <EmptyState title="No activity matches these filters." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="th-cell">Time</th>
                  <th className="th-cell">What happened</th>
                  <th className="th-cell">Record</th>
                  <th className="th-cell">Tenant</th>
                  <th className="th-cell">Trace</th>
                  <th className="th-cell" />
                </tr>
              </thead>
              <tbody>
                {rows.map((e, i) => (
                  <tr key={e.id ?? i} className="tr-hover">
                    <td className="td-cell whitespace-nowrap text-xs text-ui-text2">{fmtTime(e.occurred_at)}</td>
                    <td className="td-cell text-sm">
                      <span className="font-medium text-ui-text">{e.actor_name || e.actor_user_id}</span>{' '}
                      <Badge variant="accent">{humanize(e.activity_type)}</Badge>
                    </td>
                    <td className="td-cell font-mono text-xs text-ui-text2">{e.resource_type} · {e.resource_id}</td>
                    <td className="td-cell font-mono text-xs text-ui-text3">{e.tenant_id || '—'}</td>
                    <td className="td-cell"><TraceLink id={e.correlation_id} onTrace={onTrace} /></td>
                    <td className="td-cell">
                      <Button variant="link" size="xs" className="text-accent-text" onClick={() => setSelected(e)}>
                        Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PagedFooter skip={skip} total={total} onSkip={setSkip} />

      {selected && (
        <Modal title="Activity" onClose={() => setSelected(null)} maxWidthClassName="sm:max-w-2xl">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <DetailRow label="Time">{fmtTime(selected.occurred_at)}</DetailRow>
            <DetailRow label="Activity">{humanize(selected.activity_type)}</DetailRow>
            <DetailRow label="User">{selected.actor_name || selected.actor_user_id}</DetailRow>
            <DetailRow label="Record">{selected.resource_type} {selected.resource_id}</DetailRow>
            <DetailRow label="Tenant">{selected.tenant_id}</DetailRow>
            <DetailRow label="Trace">
              <TraceLink id={selected.correlation_id} onTrace={(id) => { setSelected(null); onTrace(id); }} />
            </DetailRow>
          </div>
          {selected.detail && Object.keys(selected.detail).length > 0 && <JsonBlock value={selected.detail} />}
        </Modal>
      )}
    </div>
  );
}
